import { useEffect, useRef, useCallback } from 'react';

const DEFAULT_EVENTS = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'];

export function useIdleTimer(onIdle, timeoutMs, events = DEFAULT_EVENTS) {
  const timerRef = useRef(null);
  const onIdleRef = useRef(onIdle); // Use ref to avoid effect dependency issues if onIdle changes

  // Update the ref if the onIdle callback changes
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  // Function to manually clear the timer if needed (e.g., on logout)
  const clear = useCallback(() => {
    clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    // Guard clause – disable the timer when timeoutMs is null/undefined/≤0
    if (!timeoutMs || timeoutMs <= 0) {
      clear(); // Ensure any existing timer is cleared
      return;  // Exit the effect, don't attach listeners or start timers
    }

    // Handler to reset the timer on activity
    const handleActivity = () => {
      clear(); // Clear the previous timer
      timerRef.current = setTimeout(() => {
        // Execute the onIdle callback via the ref
        if (onIdleRef.current) {
            onIdleRef.current();
        }
      }, timeoutMs);
    };

    // Attach listeners
    events.forEach(event =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    // Start the first timer cycle immediately when the effect runs with a valid timeout
    handleActivity();

    // Cleanup function
    return () => {
      clear(); // Clear timer on cleanup
      events.forEach(event =>
        window.removeEventListener(event, handleActivity)
      );
    };
    // Dependencies: onIdleRef (via useEffect above), timeoutMs, events, clear
    // Including `clear` ensures the effect updates if its definition changes (though unlikely here)
  }, [timeoutMs, events, clear, onIdle]); // Add onIdle here as it's used indirectly via onIdleRef


  return { clear }; // Return clear function
} 